import { useEffect, useRef, useState } from 'react';
import { Search, Trash2, X } from 'lucide-react';
import { api, ApiError, type Domain, type MailboxAdminRow } from '../../lib/api';
import type { ToastKind } from '../../components/ui/Toast';

export function MailboxManager({
  t,
  toast,
  domains,
}: {
  t: (k: any) => string;
  toast: (message: string, kind?: ToastKind) => void;
  domains: Domain[];
}) {
  const [rows, setRows] = useState<MailboxAdminRow[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [domainFilter, setDomainFilter] = useState<number | ''>('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = async (searchVal: string, domainVal: number | '') => {
    try {
      const res = await api.adminListMailboxes({
        search: searchVal || undefined,
        domainId: domainVal === '' ? undefined : domainVal,
        limit: 100,
      });
      setRows(res.rows);
      setTotal(res.total);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : t('errorGeneric'), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load('', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search
  const onSearchChange = (val: string) => {
    setSearch(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void load(val, domainFilter);
    }, 300);
  };

  const onDomainChange = (val: number | '') => {
    setDomainFilter(val);
    void load(search, val);
  };

  const clearFilters = () => {
    setSearch('');
    setDomainFilter('');
    void load('', '');
  };

  const remove = async (m: MailboxAdminRow) => {
    if (!window.confirm(t('confirmDeleteMailboxAdmin'))) return;
    setBusy(true);
    try {
      await api.adminDeleteMailbox(m.id);
      toast(t('deletedMailbox'), 'success');
      void load(search, domainFilter);
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t('errorGeneric'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const hasFilter = !!search || domainFilter !== '';

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">{t('manageMailboxesDesc')}</p>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            className="input pl-9"
            placeholder={t('searchMailbox')}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
        <select
          className="input sm:w-48"
          value={domainFilter}
          onChange={(e) => onDomainChange(e.target.value === '' ? '' : Number(e.target.value))}
        >
          <option value="">{t('filterDomain')}</option>
          {domains.map((d) => (
            <option key={d.id} value={d.id}>
              {d.domain_name}
            </option>
          ))}
        </select>
        {hasFilter && (
          <button type="button" className="btn-ghost shrink-0" onClick={clearFilters} title={t('clear')}>
            <X className="h-4 w-4" />
            {t('clear')}
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">{t('loading')}</div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500 dark:bg-slate-900 dark:text-slate-400">
                <tr>
                  <th className="px-3 py-2">{t('address')}</th>
                  <th className="px-3 py-2">{t('emailCount')}</th>
                  <th className="px-3 py-2">{t('lastAccess')}</th>
                  <th className="px-3 py-2">{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => (
                  <tr key={m.id} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="px-3 py-2 font-mono break-all">{m.address}</td>
                    <td className="px-3 py-2 tabular-nums">{m.email_count}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-500">{m.last_access_at}</td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        className="btn-ghost text-rose-600"
                        disabled={busy}
                        onClick={() => void remove(m)}
                        title={t('delete')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-slate-500">
                      {t('noMailboxes')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-400">
            {total} {t('mailboxesAdmin').toLowerCase()}
            {rows.length < total ? ` · ${rows.length} shown` : ''}
          </p>
        </>
      )}
    </div>
  );
}
